import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export function useOfflineDataPrep() {
  const [isPrepping, setIsPrepping] = useState(false);
  const [progress, setProgress] = useState(0);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    const fetchTenantId = async () => {
      if (!user) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .single();
      
      if (profile) {
        setTenantId(profile.tenant_id);
      }
    };
    
    fetchTenantId();
  }, [user]);

  useEffect(() => {
    if (user && tenantId && navigator.onLine) {
      prepCriticalData();
    }
  }, [user, tenantId]);

  const prepCriticalData = async () => {
    if (isPrepping || !tenantId) return;
    
    setIsPrepping(true);
    setProgress(0);

    try {
      // 1. Fetch recent opportunities (25%)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: opportunities } = await supabase
        .from('opportunities')
        .select('*, pipeline_stage(*), company(*), contact(*)')
        .eq('tenant_id', tenantId)
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      queryClient.setQueryData(['opportunities', tenantId], opportunities);
      setProgress(25);

      // 2. Fetch all contacts (50%)
      const { data: contacts } = await supabase
        .from('contacts')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('name');

      queryClient.setQueryData(['contacts', tenantId], contacts);
      setProgress(50);

      // 3. Fetch active companies (75%)
      const { data: companies } = await supabase
        .from('companies')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');

      queryClient.setQueryData(['companies', tenantId], companies);
      setProgress(75);

      // 4. Fetch recent emails (100%)
      const { data: emails } = await supabase
        .from('email_messages')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('received_at', { ascending: false })
        .limit(50);

      queryClient.setQueryData(['emails', tenantId], emails);
      setProgress(100);

      // Store last sync timestamp
      localStorage.setItem('last-offline-prep', new Date().toISOString());
    } catch (error) {
      console.error('Failed to prep offline data:', error);
    } finally {
      setIsPrepping(false);
    }
  };

  const getLastSyncTime = (): Date | null => {
    const lastSync = localStorage.getItem('last-offline-prep');
    return lastSync ? new Date(lastSync) : null;
  };

  return {
    isPrepping,
    progress,
    prepCriticalData,
    lastSyncTime: getLastSyncTime(),
  };
}
